// LLM generated - Model Claude 4 Sonnet
import { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Flex, Stack, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconLock } from '@tabler/icons-react';

import dt from '~/testing';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import TextInputModal from '~/domains/design-system/components/TextInputModal/TextInputModal';
import bannerWordsRefine from '../utils/bannedWordsRefine';

import type { StaticFieldProps } from '../../types/common';

import type { InputModalField } from './types';
import { useStyles } from './InputModalField.styles';

export function InputModalFieldElement({
  field,
  value,
  onValueChange,
  errors,
  isTouched,
  isOpen: externalIsOpen,
  onOpenChange,
}: StaticFieldProps<InputModalField> & {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const prevIsOpenRef = useRef(false);

  // Use external control if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;

  const isError = isTouched && errors;
  const { cx, classes } = useStyles();
  const theme = useMantineTheme();

  const displayValue = typeof value === 'string' ? value : '';
  const renderTrigger = field.renderTrigger !== false; // Default to true

  const handleOpen = useCallback(() => {
    setLocalValue(displayValue);
    setIsOpen(true);
  }, [displayValue, setIsOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setLocalValue('');
  }, [setIsOpen]);

  const handleSave = useCallback(() => {
    const trimmedValue = localValue.trim();
    if (trimmedValue && trimmedValue !== displayValue) {
      onValueChange(trimmedValue);
    }
    handleClose();
  }, [localValue, displayValue, onValueChange, handleClose]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const isDisabled = !localValue.trim() || localValue.trim() === displayValue;

  // Initialize localValue when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setLocalValue(displayValue);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, displayValue]);

  // Validation function for max length if specified
  const validate = useCallback(
    (inputValue: string) => {
      if (field.validation?.maxLength && inputValue.trim().length > field.validation.maxLength) {
        return `Must be ${field.validation.maxLength} characters or less`;
      }
      if (field.validation?.minLength && inputValue.trim().length < field.validation.minLength) {
        return `Must be at least ${field.validation.minLength} characters`;
      }

      // Banned words validation
      if (field.validation?.bannedWords) {
        const bannedWordsCheck = bannerWordsRefine(field.validation.bannedWords);
        if (!bannedWordsCheck(inputValue)) {
          return `Your ${
            field.label?.toLowerCase() || 'input'
          } cannot contain the following phrase: ${inputValue.trim()}`;
        }
      }

      // Banned words (hidden) validation
      if (field.validation?.bannedWordsHidden) {
        const bannedWordsHiddenCheck = bannerWordsRefine(
          field.validation.bannedWordsHidden,
          field.validation.softWords,
          { cleanValue: false }
        );
        if (!bannedWordsHiddenCheck(inputValue)) {
          return `Your ${
            field.label?.toLowerCase() || 'input'
          } has a word or phrase that you cannot use.`;
        }
      }

      // Custom validator (if provided)
      if (field.validation?.customValidator) {
        const customError = field.validation.customValidator(inputValue.trim());
        if (customError) {
          return customError;
        }
      }
      return null;
    },
    [
      field.validation?.maxLength,
      field.validation?.minLength,
      field.validation?.bannedWords,
      field.validation?.bannedWordsHidden,
      field.validation?.softWords,
      field.validation?.customValidator,
      field.label,
    ]
  );

  const modalContent = (
    <TextInputModal
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSave}
      value={localValue}
      onChange={handleInputChange}
      title={field?.label}
      subtitle={field?.description}
      placeholder={field.placeholder}
      buttonText={field.buttonText || 'Save'}
      buttonDisabled={isDisabled}
      validate={validate}
    />
  );

  // If renderTrigger is false, just return the modal
  if (!renderTrigger) {
    return modalContent;
  }

  // Otherwise render the full card button + modal
  return (
    <div
      className={classes.wrapper}
      data-test-id={dt.contest.create.accordion}
      onBlur={() => setHover(false)}
      onFocus={() => setHover(!hover)}
      onClick={() => setHover(!hover)}
      onKeyDown={() => setHover(!hover)}
      onMouseLeave={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      role="button"
      tabIndex={0}
    >
      <Button
        variant="text"
        data-test-id={dt.contest.create.selectOption}
        disabled={field?.disabled}
        onClick={handleOpen}
        className={cx(classes.card, { [classes.error]: isTouched && errors })}
      >
        <Flex align="center" justify="space-between">
          <Stack spacing={0}>
            <Flex align="center" gap={theme.other.spacing._4}>
              {field.indicator && <Box className={classes.indicator} />}
              <Text variant="body-small" c={theme.colors.gray[6]}>
                {field.label}
              </Text>
            </Flex>
            <Text variant="title-medium" c={isError ? theme.colors.red[6] : 'inherit'}>
              {isError ? errors.join(', ') : displayValue || 'Not set'}
            </Text>
          </Stack>

          {field?.locked ? (
            <IconLock size={24} stroke={theme.colors.gray[4]} strokeWidth={1.5} />
          ) : (
            <IconChevronDown size={18} stroke={theme.colors.gray[4]} />
          )}
        </Flex>
        {field?.locked && hover ? (
          <div className={classes.tooltip}>
            <Text className={classes.tooltipLabel} variant="body-medium">
              {field?.locked}
            </Text>
          </div>
        ) : null}
      </Button>
      {modalContent}
    </div>
  );
}
